import React, { useState, useEffect, useContext, createContext } from 'react';

let modelsCache = null;
let modelsPromise = null;

// Load models data — deferred slightly to let critical resources load first
function loadModelsData() {
  if (modelsCache) return Promise.resolve(modelsCache);
  if (!modelsPromise) {
    modelsPromise = import('../data/models.json').then(module => {
      modelsCache = module.default;
      return modelsCache;
    });
  }
  return modelsPromise;
}

// Defer the initial load to avoid competing with hero image and fonts
if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
  requestIdleCallback(() => loadModelsData());
} else {
  setTimeout(() => loadModelsData(), 50);
}

const ModelsContext = createContext({ models: [], loading: true });

export function ModelsProvider({ children }) {
  const [models, setModels] = useState(modelsCache || []);
  const [loading, setLoading] = useState(!modelsCache);

  useEffect(() => {
    if (modelsCache) {
      setModels(modelsCache);
      setLoading(false);
      return;
    }
    loadModelsData().then(data => {
      setModels(data);
      setLoading(false);
    });
  }, []);

  return (
    <ModelsContext.Provider value={{ models, loading }}>
      {children}
    </ModelsContext.Provider>
  );
}

/**
 * Hook to access models data.
 * Returns the full models array (empty while loading).
 * Use useModelsWithLoading() if you need the loading state.
 */
export function useModels() {
  const { models } = useContext(ModelsContext);
  return models;
}

/**
 * Hook that returns { models, loading } for components
 * that want to show loading indicators.
 */
export function useModelsWithLoading() {
  return useContext(ModelsContext);
}
